import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MillosLogo from '../components/MillosLogo';
import { DESIGN_TOKENS } from '../theme';
import { USER_ROLES } from '../userRoles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExtractedCert {
  property_address?: string;
  certificate_type?: string;
  certificate_type_category?: string;
  issue_date?: string;
  expiry_date?: string;
}

interface OnboardingData {
  role: string | null;
  portfolioSize: string | null;
  extractedCert: ExtractedCert | null;
  providerName: string;
  providerEmail: string;
  providerCertTypes: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ['YOU', 'PORTFOLIO', 'PROPERTIES', 'PROVIDERS', 'CONNECT'];

const ROLES = USER_ROLES;

const PORTFOLIO_SIZES = ['1', '2–5', '6–20', '21–100', '100+'];
const PLAN_MAP: Record<string, string> = {
  '1': 'Free',
  '2–5': 'Pro',
  '6–20': 'Pro',
  '21–100': 'Business',
  '100+': 'Enterprise',
};

const CERT_TYPES = [
  'Gas Certificate', 'Electrical Certificate', 'Energy Certificate',
  'Fire Certificate', 'Smoke Certificate', 'Insurance Certificate',
  'Licence Certificate', 'Deposit Protection Certificate', 'Other',
];

// ─── Stepper ─────────────────────────────────────────────────────────────────

const Stepper: React.FC<{ current: number }> = ({ current }) => {
  const { palette } = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        px: 4,
        pt: 4,
        pb: 3,
        gap: 0,
      }}
    >
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '14px',
                border: i < current
                  ? 'none'
                  : i === current
                  ? `2px solid ${palette.tokens.ink}`
                  : '2px solid #d1d5db',
                backgroundColor: i < current ? '#6366f1' : i === current ? palette.tokens.ink : 'transparent',
                color: i <= current ? palette.tokens.white : palette.tokens.mutedSoft,
                transition: 'all 0.3s',
                flexShrink: 0,
              }}
            >
              {i < current ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
            </Box>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: i === current ? 700 : 500,
                letterSpacing: '0.08em',
                color: i === current ? palette.tokens.ink : i < current ? '#6366f1' : palette.tokens.mutedSoft,
                mt: 0.75,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
          </Box>
          {i < STEPS.length - 1 && (
            <Box
              sx={{
                height: '2px',
                flex: 1,
                backgroundColor: i < current ? '#6366f1' : palette.tokens.hairline,
                mt: '17px',
                transition: 'background-color 0.3s',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

// ─── CopilotSays — agent avatar + speech bubble ────────────────────────────────

const CopilotSays: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { palette } = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3.5 }}>
      {/* Agent avatar */}
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          backgroundColor: '#1e1b4b',
          border: '2.5px solid #6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 0 0 4px rgba(99,102,241,0.12)',
        }}
      >
        <MillosLogo size={32} agent />
      </Box>
      {/* Speech bubble */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: palette.tokens.canvas,
          border: `1.5px solid ${palette.tokens.hairline}`,
          borderRadius: '4px 14px 14px 14px',
          px: 2.5,
          py: 2,
        }}
      >
        <Typography sx={{ fontSize: '15px', color: palette.tokens.ink, lineHeight: 1.65, fontWeight: 500 }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const firstName = (user as any)?.name?.split(' ')[0]?.replace(/^./, (c: string) => c.toUpperCase()) || 'there';
  const { palette } = useTheme();

  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    axios.get(`${config.apiUrl}/api/auth/google/scopes`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const scopes: string[] = res.data.scopes || [];
      setGmailConnected(scopes.some(s => s.includes('gmail') || s.includes('mail')));
      setCalendarConnected(scopes.some(s => s.includes('calendar')));
    }).catch(() => {});
  }, [token]);

  const [data, setData] = useState<OnboardingData>({
    role: null,
    portfolioSize: null,
    extractedCert: null,
    providerName: '',
    providerEmail: '',
    providerCertTypes: [],
  });

  const update = (patch: Partial<OnboardingData>) =>
    setData(prev => ({ ...prev, ...patch }));

  // ── Certificate upload ──────────────────────────────────────────────────────

  const processFile = useCallback(async (file: File) => {
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Please upload a PDF file.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const res = await axios.post(`${config.apiUrl}/api/bulk-upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = res.data.results?.[0];
      if (result) {
        let d: any = null;

        if (result.status === 'no_match') {
          d = result.certificate_data?.[0];
          if (d && result.file_info) {
            await axios.post(`${config.apiUrl}/api/pending-renewals/add-as-new`, {
              cert_data: d,
              file_info: result.file_info,
            }, { headers: { Authorization: `Bearer ${token}` } });
          }
        } else if (result.status === 'duplicate') {
          d = result.extracted_data && Object.keys(result.extracted_data).length > 0
            ? result.extracted_data
            : result.duplicate_info;
        } else if (result.status === 'match_found') {
          const pr = result.pending_renewals?.[0];
          d = pr ? { certificate_type: pr.type, property_address: pr.address } : null;
        }

        if (d) {
          update({
            extractedCert: {
              property_address: d.property_address ?? d.full_address,
              certificate_type: d.certificate_type,
              certificate_type_category: d.certificate_type_category,
              issue_date: d.issue_date,
              expiry_date: d.expiry_date,
            },
            providerCertTypes: d.certificate_type_category ? [d.certificate_type_category] : [],
          });
        }
      }
    } catch {
      setUploadError('Could not process the file. You can continue and add it later.');
    } finally {
      setUploading(false);
    }
  }, [token]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // ── Provider save ───────────────────────────────────────────────────────────

  const saveProvider = async () => {
    if (!data.providerName && !data.providerEmail) return;
    setSavingProvider(true);
    try {
      await axios.post(`${config.apiUrl}/api/certificate-providers`, {
        provider_name: data.providerName,
        provider_email: data.providerEmail,
        certificate_types: data.providerCertTypes,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // non-critical
    } finally {
      setSavingProvider(false);
    }
  };

  // ── Navigation ──────────────────────────────────────────────────────────────

  const saveRole = async () => {
    if (!data.role) return;
    try {
      await axios.patch(`${config.apiUrl}/api/profile`, { user_role: data.role }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // non-critical: the role can be set again from the profile page
    }
  };

  const handleContinue = async () => {
    if (step === 0) await saveRole();
    if (step === 3) await saveProvider();
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      navigate('/dashboard', {
        state: {
          fromOnboarding: true,
          googleConnected: gmailConnected && calendarConnected,
          uploadedCert: data.extractedCert,
        },
      });
    }
  };

  const handleBack = () => setStep(s => s - 1);
  const handleSkip = () => navigate('/dashboard');

  const handleGoogleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/auth/google/start`, {
        redirect_uri: `${window.location.origin}/auth/google/callback`,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const popup = window.open(res.data.auth_url, '_blank', 'width=520,height=640');
      const poll = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(poll);
          try {
            const scopesRes = await axios.get(`${config.apiUrl}/api/auth/google/scopes`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const scopes: string[] = scopesRes.data.scopes || [];
            setGmailConnected(scopes.some(s => s.includes('gmail') || s.includes('mail')));
            setCalendarConnected(scopes.some(s => s.includes('calendar')));
          } finally {
            setConnecting(false);
          }
        }
      }, 600);
    } catch {
      setConnecting(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const toggleCertType = (type: string) => {
    update({
      providerCertTypes: data.providerCertTypes.includes(type)
        ? data.providerCertTypes.filter(t => t !== type)
        : [...data.providerCertTypes, type],
    });
  };

  const formatDate = (d?: string) => {
    if (!d) return '—';
    if (d.includes('/')) return d;
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  // ─── Step content ────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {

      // ── Step 0: WHO ARE YOU ─────────────────────────────────────────────────
      case 0:
        return (
          <>
            <CopilotSays>
              Hi {firstName}! I'm your copilot. I'll make sure no certificate ever expires on your watch. First, tell me how you work.
            </CopilotSays>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {ROLES.map(role => (
                <RoleCard
                  key={role.id}
                  selected={data.role === role.id}
                  onClick={() => update({ role: role.id })}
                  label={role.label}
                  desc={role.desc}
                />
              ))}
            </Box>
          </>
        );

      // ── Step 1: PORTFOLIO SIZE ───────────────────────────────────────────────
      case 1: {
        const roleLabel = ROLES.find(r => r.id === data.role)?.label;
        return (
          <>
            <CopilotSays>
              {roleLabel ? <><strong>{roleLabel}</strong> — noted. </> : null}
              How many properties are you managing? I'll scale your cockpit to match.
            </CopilotSays>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {PORTFOLIO_SIZES.map(size => (
                <Button
                  key={size}
                  onClick={() => update({ portfolioSize: size })}
                  variant="outlined"
                  sx={{
                    borderRadius: '10px',
                    borderColor: data.portfolioSize === size ? '#6366f1' : palette.tokens.hairline,
                    color: data.portfolioSize === size ? '#6366f1' : '#374151',
                    backgroundColor: data.portfolioSize === size ? '#eef2ff' : palette.tokens.white,
                    fontWeight: data.portfolioSize === size ? 700 : 500,
                    fontSize: '15px',
                    px: 3,
                    py: 1.5,
                    minWidth: 80,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#6366f1', backgroundColor: '#f5f3ff' },
                  }}
                >
                  {size}
                </Button>
              ))}
            </Box>
            {data.portfolioSize && (
              <Typography sx={{ mt: 2, fontSize: '13px', color: palette.tokens.muted }}>
                Recommended plan:{' '}
                <Box component="span" sx={{ fontWeight: 700, color: palette.tokens.ink }}>
                  {PLAN_MAP[data.portfolioSize]}
                </Box>
              </Typography>
            )}
          </>
        );
      }

      // ── Step 2: PROPERTIES / CERT UPLOAD ────────────────────────────────────
      case 2:
        return (
          <>
            <CopilotSays>
              {data.extractedCert ? (
                <>
                  I found a{' '}
                  <strong>
                    {data.extractedCert.certificate_type_category || data.extractedCert.certificate_type || 'certificate'}
                  </strong>
                  {data.extractedCert.property_address ? (
                    <> at <strong>{data.extractedCert.property_address}</strong></>
                  ) : null}
                  . I've saved it to your dashboard — you can add more after setup.
                </>
              ) : (
                <>
                  Let me take a look at your first certificate. Drop any compliance PDF below — I'll extract the property, type, and dates in seconds.
                </>
              )}
            </CopilotSays>

            {/* Upload zone */}
            {!data.extractedCert && !uploading && (
              <Box
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${dragOver ? '#6366f1' : '#d1d5db'}`,
                  borderRadius: '14px',
                  backgroundColor: dragOver ? '#eef2ff' : '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 5,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#6366f1', backgroundColor: '#eef2ff' },
                }}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 36, color: palette.tokens.mutedSoft, mb: 1.5 }} />
                <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '15px' }}>
                  Drop certificate here
                </Typography>
                <Typography sx={{ fontSize: '13px', color: palette.tokens.mutedSoft, mt: 0.5 }}>
                  PDF only · one file
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={handleFilePick}
                />
              </Box>
            )}

            {/* Uploading spinner */}
            {uploading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
                <Typography sx={{ color: palette.tokens.muted, fontSize: '14px' }}>AI is reading your certificate…</Typography>
              </Box>
            )}

            {/* Extracted data card */}
            {data.extractedCert && !uploading && (
              <Box
                sx={{
                  border: '1.5px solid #c7d2fe',
                  borderRadius: '14px',
                  backgroundColor: '#eef2ff',
                  p: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TaskAltIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#4338ca', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Extracted data
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <ExtractedField label="Property" value={data.extractedCert.property_address} />
                  <ExtractedField label="Certificate type" value={data.extractedCert.certificate_type_category} />
                  <ExtractedField label="Issue date" value={formatDate(data.extractedCert.issue_date)} />
                  <ExtractedField label="Expiry date" value={formatDate(data.extractedCert.expiry_date)} />
                </Box>
                <Button
                  size="small"
                  onClick={() => { update({ extractedCert: null, providerCertTypes: [] }); setUploadError(null); }}
                  sx={{ mt: 2, color: '#6366f1', textTransform: 'none', fontSize: '12px', p: 0 }}
                >
                  Upload a different file
                </Button>
              </Box>
            )}

            {uploadError && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>{uploadError}</Alert>
            )}

            <Typography sx={{ mt: 2.5, fontSize: '12px', color: palette.tokens.mutedSoft }}>
              You can also skip this and upload certificates from your dashboard.
            </Typography>
          </>
        );

      // ── Step 3: PROVIDERS ────────────────────────────────────────────────────
      case 3: {
        const suggestedType = data.extractedCert?.certificate_type_category;
        return (
          <>
            <CopilotSays>
              {suggestedType ? (
                <>
                  Now tell me who sends your <strong>{suggestedType}s</strong>. Once I know their email address, I will manage the renewals — automatically.
                </>
              ) : (
                <>
                  Now tell me who sends your certificates. Once I know their email address, I will manage the renewals — automatically.
                </>
              )}
            </CopilotSays>

            <Box
              sx={{
                border: `1.5px solid ${palette.tokens.hairline}`,
                borderRadius: '14px',
                p: 2.5,
                backgroundColor: '#fafafa',
              }}
            >
              {/* Provider name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: '8px',
                    backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessCenterOutlinedIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                </Box>
                <TextField
                  fullWidth
                  placeholder="Engineer or company name (e.g. North London Gas)"
                  value={data.providerName}
                  onChange={e => update({ providerName: e.target.value })}
                  variant="outlined"
                  size="small"
                  sx={fieldSx}
                />
              </Box>

              {/* Provider email */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 36, height: 36 }} />
                <TextField
                  fullWidth
                  type="email"
                  placeholder="certificates@provider.co.uk"
                  value={data.providerEmail}
                  onChange={e => update({ providerEmail: e.target.value })}
                  variant="outlined"
                  size="small"
                  sx={fieldSx}
                />
              </Box>

              {/* Cert types */}
              <Box sx={{ pl: '52px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: palette.tokens.mutedSoft, textTransform: 'uppercase', mb: 1 }}>
                  Certificate types
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CERT_TYPES.map(type => {
                    const selected = data.providerCertTypes.includes(type);
                    return (
                      <Chip
                        key={type}
                        label={type}
                        onClick={() => toggleCertType(type)}
                        size="small"
                        sx={{
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: selected ? 600 : 400,
                          backgroundColor: selected ? '#6366f1' : '#f3f4f6',
                          color: selected ? palette.tokens.white : '#374151',
                          border: 'none',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: selected ? '#4f46e5' : palette.tokens.hairline },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Typography sx={{ mt: 2, fontSize: '12px', color: palette.tokens.mutedSoft }}>
              You can add more providers later in <strong>Integrations → Providers</strong>.
            </Typography>
          </>
        );
      }

      // ── Step 4: CONNECT GOOGLE ───────────────────────────────────────────────
      case 4:
        return (
          <>
            <CopilotSays>
              {gmailConnected && calendarConnected
                ? "You're all connected — I can now watch your inbox and sync reminders to your calendar. Click Enter cockpit to get started."
                : "Connect your Google account so I can detect new certificates from your Gmail and add reminders straight to your Calendar."}
            </CopilotSays>

            {/* Gmail row */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ConnectRow
                icon={<EmailOutlinedIcon sx={{ fontSize: 20, color: gmailConnected ? palette.tokens.success : palette.tokens.muted }} />}
                label="Gmail"
                desc="Auto-detect certificates from your inbox"
                connected={gmailConnected}
              />
              <ConnectRow
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: calendarConnected ? palette.tokens.success : palette.tokens.muted }} />}
                label="Google Calendar"
                desc="Sync expiry reminders to your calendar"
                connected={calendarConnected}
              />
            </Box>

            {!(gmailConnected && calendarConnected) && (
              <Button
                onClick={handleGoogleConnect}
                disabled={connecting}
                variant="contained"
                fullWidth
                sx={{
                  mt: 2.5,
                  backgroundColor: palette.tokens.ink,
                  borderRadius: '10px',
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: palette.tokens.surfaceDarkElevated, boxShadow: 'none' },
                }}
              >
                {connecting ? <CircularProgress size={18} sx={{ color: palette.tokens.white }} /> : 'Connect Google Account'}
              </Button>
            )}

            <Typography
              onClick={handleSkip}
              sx={{ mt: 2, fontSize: '12px', color: palette.tokens.mutedSoft, textAlign: 'center', cursor: 'pointer', '&:hover': { color: palette.tokens.muted } }}
            >
              I'll connect later from the Integrations page →
            </Typography>
          </>
        );

      default:
        return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#eef2fb', display: 'flex', flexDirection: 'column' }}>
      {/* Skip link */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 4, pt: 3 }}>
        <Button
          onClick={handleSkip}
          sx={{ color: palette.tokens.mutedSoft, textTransform: 'none', fontSize: '13px', fontWeight: 500, '&:hover': { color: palette.tokens.muted, backgroundColor: 'transparent' } }}
        >
          Skip onboarding →
        </Button>
      </Box>

      {/* Stepper */}
      <Box sx={{ maxWidth: 680, mx: 'auto', width: '100%', px: 2 }}>
        <Stepper current={step} />
      </Box>

      {/* Card */}
      <Box
        sx={{
          maxWidth: 680,
          mx: 'auto',
          width: '100%',
          px: 2,
          pb: 6,
          flex: 1,
        }}
      >
        <Box
          sx={{
            backgroundColor: palette.tokens.white,
            borderRadius: '16px',
            px: { xs: 3, sm: 5 },
            pt: 4,
            pb: 4,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          {renderStep()}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #f3f4f6' }}>
            <Button
              onClick={handleBack}
              disabled={step === 0}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: palette.tokens.muted,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': { backgroundColor: palette.tokens.canvas },
                '&:disabled': { opacity: 0 },
              }}
            >
              Back
            </Button>

            <Button
              onClick={handleContinue}
              disabled={savingProvider}
              endIcon={isLastStep ? <ShieldOutlinedIcon /> : <ArrowForwardIcon />}
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                borderRadius: '10px',
                px: 3,
                py: 1.25,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#4f46e5', boxShadow: 'none' },
              }}
            >
              {savingProvider
                ? <CircularProgress size={18} sx={{ color: palette.tokens.white }} />
                : isLastStep
                ? 'Enter cockpit'
                : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Small sub-components ─────────────────────────────────────────────────────

const RoleCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  label: string;
  desc: string;
}> = ({ selected, onClick, label, desc }) => {
  const { palette } = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        border: `1.5px solid ${selected ? '#6366f1' : palette.tokens.hairline}`,
        borderRadius: '12px',
        px: 2.5,
        py: 2,
        cursor: 'pointer',
        textAlign: 'center',
        backgroundColor: selected ? '#f5f3ff' : palette.tokens.white,
        transition: 'all 0.15s',
        '&:hover': { borderColor: '#6366f1', backgroundColor: '#f5f3ff' },
        '&:hover .role-desc, &:focus-within .role-desc': { gridTemplateRows: '1fr', opacity: 1 },
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '16px', color: palette.tokens.ink, textAlign: 'center' }}>{label}</Typography>
      {/* 0fr -> 1fr animates to the text's real height, so nothing is ever clipped */}
      <Box
        className="role-desc"
        sx={{
          display: 'grid',
          gridTemplateRows: selected ? '1fr' : '0fr',
          opacity: selected ? 1 : 0,
          transition: 'grid-template-rows 0.2s ease, opacity 0.2s ease',
        }}
      >
        <Typography sx={{ overflow: 'hidden', minHeight: 0, fontSize: '14px', color: palette.tokens.muted, pt: 0.5, textAlign: 'center' }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  );
};

const ExtractedField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: '#818cf8', textTransform: 'uppercase', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e1b4b' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const ConnectRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  desc: string;
  connected: boolean;
}> = ({ icon, label, desc, connected }) => {
  const { palette } = useTheme();
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        border: `1.5px solid ${connected ? '#bbf7d0' : palette.tokens.hairline}`,
        borderRadius: '12px',
        px: 2.5, py: 1.75,
        backgroundColor: connected ? '#f0fdf4' : '#fafafa',
        transition: 'all 0.2s',
      }}
    >
      <Box
        sx={{
          width: 38, height: 38, borderRadius: '10px',
          backgroundColor: connected ? '#dcfce7' : '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: palette.tokens.ink }}>{label}</Typography>
        <Typography sx={{ fontSize: '12px', color: palette.tokens.muted }}>{desc}</Typography>
      </Box>
      {connected && (
        <Chip label="Connected" size="small" sx={{ backgroundColor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '11px', border: 'none' }} />
      )}
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: DESIGN_TOKENS.white,
    fontSize: '14px',
    '& fieldset': { borderColor: DESIGN_TOKENS.hairline },
    '&:hover fieldset': { borderColor: '#d1d5db' },
    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
  },
};

export default OnboardingPage;
